<template>
  <b-modal
    id="filterModal"
    size="lg"
    :title="`Filter: ${filter_layer}`"
    content-class="menu-bg-blue-transparent-modal filter-modal-content"
    header-class="filter-modal-header"
    body-class="filter-modal-body"
    hide-footer
    @hidden="closed"
  >
    <!-- Export actions -->
    <div
      v-if="globalIsUserAllowed('Layer_Filter_Export_Shapefile') || (isErrorsLayer && globalIsUserAllowed('Layer_Filter_Export_CSV'))"
      class="filter-export-bar"
    >
      <b-button
        v-if="isErrorsLayer && globalIsUserAllowed('Layer_Filter_Export_CSV')"
        variant="warning"
        class="filter-btn"
        @click="exportFilterFile('csv')"
      >
        <fa :icon="['fas', 'download']" class="mr-1" /> Export CSV
      </b-button>
      <b-button
        v-if="globalIsUserAllowed('Layer_Filter_Export_Shapefile')"
        variant="warning"
        class="filter-btn"
        @click="exportFilterFile('shape')"
      >
        <fa :icon="['fas', 'download']" class="mr-1" /> Export Shapefile
      </b-button>
    </div>

    <!-- Filter rule builder -->
    <div class="filter-builder">
      <span class="filter-builder-title">Add a filter rule</span>
      <b-row class="align-items-end no-gutters">
        <b-col class="col-sm-4 pr-2">
          <label class="filter-field-label">Property</label>
          <b-form-input
            list="property_list"
            name="properties"
            id="properties"
            class="form-control"
            placeholder="Property"
            v-model="filter_property"
          />
          <b-datalist id="property_list">
            <option value=""></option>
            <b-form-select-option
              :key="i"
              :value="prop.name"
              v-for="(prop, i) in layerProperties"
            >
              {{ prop.name }}
            </b-form-select-option>
          </b-datalist>
        </b-col>
        <b-col class="col-sm-2 pr-2">
          <label class="filter-field-label">Operator</label>
          <b-form-select
            v-model="filter_operator"
            id="operatorSelect"
            @change="checkOperator"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Operator
              </b-form-select-option>
            </template>
            <b-form-select-option
              v-for="op in operators"
              :key="op.id"
              :value="op.tag"
            >
              {{ op.tag }}
            </b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col class="col-sm-2 pr-2">
          <label class="filter-field-label">Value</label>
          <b-form-input placeholder="Value" v-model="filter_value" />
        </b-col>
        <b-col class="col-sm-2 pr-2 filter_value_2">
          <label class="filter-field-label" :class="{ 'invisible': isHidden }">Value 2</label>
          <b-form-input
            placeholder="Value"
            v-model="filter_value_2"
            :hidden="isHidden"
            name="filter_value_2"
          />
        </b-col>
        <b-col class="col-sm-2">
          <b-button variant="primary" class="filter-btn filter-add-btn w-100" @click="addRule()">
            <fa :icon="['fas', 'plus']" class="mr-1" /> Add Rule
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Displaying added filter rules as chips -->
    <div
      v-if="rules.filter(r => r.layer == filter_layer).length"
      class="active-rules"
    >
      <span class="active-rules-title">Active rules</span>
      <div class="rule-chips">
        <span
          class="rule-chip"
          v-for="(rule, i) in rules.filter(r => r.layer == filter_layer)"
          :key="i"
        >
          <span class="rule-chip-prop">{{ rule.property }}</span>
          <span class="rule-chip-op">{{ rule.operator }}</span>
          <span class="rule-chip-val">
            {{ rule.value }}<template v-if="rule.value2 != null"> &ndash; {{ rule.value2 }}</template>
          </span>
        </span>
      </div>
    </div>

    <!-- Options + hint -->
    <div class="filter-options">
      <p class="filter-hint">
        <fa :icon="['fas', 'info-circle']" class="mr-1" />
        If <strong>Maintain View</strong> is unchecked, the map zooms to bound all
        features. This can significantly impact performance when filtering dense
        layers with loose filters.
        <template v-if="filter_layer == 'errors' || filter_layer == 'errors_test'">
          <br />
          Error status and severity can be searched by exact value
          (e.g. status = active, severity = high).
        </template>
      </p>
      <b-form-checkbox v-model="maintainView" class="filter-maintain-view">
        Maintain View
      </b-form-checkbox>
    </div>

    <!-- Footer actions -->
    <div class="filter-footer">
      <b-button variant="outline-danger" class="filter-btn" @click="removeFilter">
        Remove Filter
      </b-button>
      <b-button
        variant="success"
        class="filter-btn filter-apply-btn"
        @click="generateCQL(null, true)"
      >
        Apply Filter
      </b-button>
    </div>
  </b-modal>
</template>

<style>
#filterModal .modal-lg {
  max-width: 1100px !important;
}

/* ---- Modal shell ----
   Colors (white body, dark blue #063c6d header, dark blue text) come from
   the shared .menu-bg-blue-transparent-modal theme in MapManagementPlatform.css.
   This block only adds structural polish. */
#filterModal .filter-modal-content {
  overflow: hidden;
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

#filterModal .filter-modal-header {
  padding: 1rem 1.4rem;
}

#filterModal .filter-modal-header .close {
  color: #fff;
  opacity: 0.8;
  text-shadow: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

#filterModal .filter-modal-header .close:hover {
  opacity: 1;
  transform: scale(1.1);
}

#filterModal .filter-modal-header .modal-title {
  font-weight: 600;
  font-size: 1.15rem;
  letter-spacing: 0.2px;
  text-transform: capitalize;
}

#filterModal .filter-modal-body {
  padding: 1.3rem 1.4rem 1.4rem;
  /* The global `.menu-bg-blue-transparent-modal .modal-body` forces dark-blue
     text, which is invisible on this modal's dark gradient. Default all body
     text to light; inputs override back to dark since they sit on white. */
  color: #063c6d !important;
}

/* ---- Export bar ---- */
#filterModal .filter-export-bar {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-bottom: 1rem;
}

/* ---- Rule builder card ---- */
#filterModal .filter-builder {
  background: #f4f6f8;
  border: 1px solid rgba(6, 60, 109, 0.1);
  border-radius: 12px;
  padding: 0.9rem 1rem 1rem;
  margin-bottom: 1.1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 6px 18px rgba(6, 60, 109, 0.08);
}

#filterModal .filter-builder-title {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(6, 60, 109, 0.55);
  margin-bottom: 0.7rem;
}

#filterModal .filter-field-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: rgba(6, 60, 109, 0.6);
  margin-bottom: 0.25rem;
}

/* ---- Form controls ---- */
#filterModal .form-control,
#filterModal .custom-select {
  background-color: #f4f6f8;
  border: 1px solid rgba(6, 60, 109, 0.15);
  border-radius: 8px;
  color: #063c6d;
  font-size: 0.88rem;
  box-shadow: inset 0 1px 2px rgba(6, 60, 109, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
}

#filterModal .form-control::placeholder {
  color: #8aa6bf;
}

#filterModal .form-control:hover,
#filterModal .custom-select:hover {
  border-color: rgba(74, 159, 224, 0.6);
  background-color: #fff;
}

#filterModal .form-control:focus,
#filterModal .custom-select:focus {
  border-color: #4a9fe0;
  box-shadow: 0 0 0 3px rgba(74, 159, 224, 0.2);
  background-color: #fff;
}

/* Themed dropdown caret for the operator select */
#filterModal .custom-select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%230a4a7f' d='M0 1.5 4 5.5 8 1.5z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
  background-size: 9px;
}

/* ---- Active rules as chips ---- */
#filterModal .active-rules {
  margin-bottom: 1.1rem;
}

#filterModal .active-rules-title {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(6, 60, 109, 0.55);
  margin-bottom: 0.55rem;
}

#filterModal .rule-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

#filterModal .rule-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  background: rgba(6, 60, 109, 0.06);
  border: 1px solid rgba(6, 60, 109, 0.15);
  border-radius: 999px;
  font-size: 0.82rem;
}

#filterModal .rule-chip-prop {
  font-weight: 600;
  color: #063c6d;
}

#filterModal .rule-chip-op {
  color: rgba(6, 60, 109, 0.6);
  font-weight: 500;
}

#filterModal .rule-chip-val {
  color: #063c6d;
  font-weight: 500;
}

/* ---- Options + hint ---- */
#filterModal .filter-options {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.2rem;
  padding: 0.9rem 0;
  border-top: 1px solid rgba(6, 60, 109, 0.1);
  margin-bottom: 0.5rem;
}

#filterModal .filter-hint {
  flex: 1 1 auto;
  font-size: 0.78rem;
  line-height: 1.45;
  color: rgba(6, 60, 109, 0.65);
  margin: 0;
  padding: 0.6rem 0.8rem;
  background: rgba(74, 159, 224, 0.1);
  border-left: 3px solid #4a9fe0;
  border-radius: 0 8px 8px 0;
}

#filterModal .filter-hint .svg-inline--fa {
  color: #0a5499;
}

#filterModal .filter-hint strong {
  color: #063c6d;
  font-weight: 700;
}

#filterModal .filter-maintain-view {
  flex: 0 0 auto;
  white-space: nowrap;
  color: #063c6d;
}

/* ---- Footer ---- */
#filterModal .filter-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding-top: 0.4rem;
}

/* ---- Buttons ---- */
#filterModal .filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.86rem;
  letter-spacing: 0.2px;
  padding: 0.5rem 1.15rem;
  border: none;
  text-shadow: none;
  transition: transform 0.12s ease, box-shadow 0.15s ease, filter 0.15s ease,
    background 0.15s ease, color 0.15s ease;
}

#filterModal .filter-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 4px 12px rgba(6, 60, 109, 0.2);
}

#filterModal .filter-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

/* Add Rule — primary accent */
#filterModal .filter-add-btn {
  background: linear-gradient(135deg, #4a9fe0, #2f7fc4);
  box-shadow: 0 2px 8px rgba(47, 127, 196, 0.4);
}

/* Export — amber */
#filterModal .filter-export-bar .filter-btn {
  background: linear-gradient(135deg, #f6b83d, #e69a12);
  color: #4a2f00;
}

/* Apply Filter — success */
#filterModal .filter-apply-btn {
  padding-left: 1.7rem;
  padding-right: 1.7rem;
  background: linear-gradient(135deg, #34c759, #23a344);
  box-shadow: 0 2px 8px rgba(35, 163, 68, 0.4);
}

/* Remove Filter — danger outline on the light shell */
#filterModal .filter-footer .btn-outline-danger.filter-btn {
  background: #fff;
  border: 1px solid #e5484d;
  color: #d43a3f;
}

#filterModal .filter-footer .btn-outline-danger.filter-btn:hover {
  background: #e5484d;
  border-color: #e5484d;
  color: #fff;
}
</style>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios'
import GeoJSON from 'ol/format/GeoJSON'
import VectorSource from 'ol/source/Vector'
import {get as projGet, transformExtent} from 'ol/proj'
export default {
  name: 'MMP_LayerFilterModal',
  props: [
    'filter_layer',
    'generateCQLObj',
    'isErrorsLayer',
    'layerProperties',
    'map',
    'mmp_layersObj',
    'selectedElement',
    'selsetCQL',
    'user',
    'zoomLevelCap',
    'selectedExtent'
  ],
  computed: {
    ...mapGetters(['globalUserRole','globalAllowedUserModules','globalIsUserAllowed'])
  },
  data() {
    return {
      filter_operator: null,
      filter_property: null,
      filter_value: null,
      filter_value_2: null,
      isHidden: true,
      maintainView: false,
      maintainViewArr: [],
      operators: [
        {id: 1, tag: '<'},
        {id: 2, tag: '>'},
        {id: 3, tag: '='},
        {id: 4, tag: '<>'},
        {id: 5, tag: '<='},
        {id: 6, tag: '>='},
        {id: 7, tag: 'BETWEEN'}
      ],
      rules: []
    }
  },
  methods: {
    addRule() {
      if (
        this.filter_property != null &&
        this.filter_operator != null &&
        this.filter_value != null
      ) {
        if (
          (this.filter_layer == 'errors' || this.filter_layer == 'errors_test') &&
          ['severity', 'status'].includes(this.filter_property)
        ) {
          this.filter_value = this.filter_value.toUpperCase()
          this.filter_value_2 = this.filter_value_2
            ? this.filter_value_2.toUpperCase()
            : this.filter_value_2
        }
        this.rules.push({
          layer: this.filter_layer,
          property: this.filter_property,
          operator: this.filter_operator,
          value: this.filter_value,
          value2: this.filter_value_2
        })
        this.filter_property = null
        this.filter_operator = null
        this.filter_value = null
        this.filter_value_2 = null
        this.isHidden = true
      }
    },
    checkOperator() {
      this.isHidden = this.filter_operator != 'BETWEEN'
    },
    closed() {
      let elementFilterIcon = document.getElementById(this.selectedElement)
      let elementLayerName = elementFilterIcon.parentNode.parentNode.id
      let noRulesForElement =
        this.rules.filter(r => r.layer == elementLayerName).length == 0
      let index = parseInt(this.selectedElement.split('icon')[1])
      let passedClass = {hover: true}

      if (noRulesForElement) {
        elementFilterIcon.classList.remove('orange-font')
      }

      this.$emit('filterClassesObjChanged', {
        index: index,
        passedClass: passedClass
      })
    },
    exportFilterFile(type) {
      let outputFormat = type == 'csv' ? 'CSV' : 'SHAPE-ZIP'
      let arr = this.mmp_layersObj[this.filter_layer].layer.getSource()
      let url = arr.urls[0].split('wms')[0]
      let layerType =
        this.mmp_layersObj[this.filter_layer].layer.getSource().params_.LAYERS
      let extent = this.map.getView().calculateExtent(this.map.getSize())
      let filterExtent = new transformExtent(
        extent,
        new projGet('EPSG:3857'),
        new projGet('EPSG:4326')
      )
      extent = extent.join(',')
      // Takes the filtered extent for the map to download the shapefile.
      filterExtent = filterExtent.join(',')
      let layerFilter = ''
      let currentLayer = ''
      let currentLayerFilter = ''
      this.rules
        .filter(r => r.layer == this.filter_layer)
        .forEach(i => {
          currentLayer = `${i.layer}`
          if (i.value2 == null) {
            currentLayerFilter = `${i.property} ${i.operator} '${i.value}'`
          } else {
            currentLayerFilter = `${i.property} >= '${i.value}' AND ${i.property} <= '${i.value2}'`
          }
          if (layerFilter == '') {
            layerFilter += currentLayerFilter
          } else {
            layerFilter += ' AND ' + currentLayerFilter
          }
        })

      if (this.isErrorsLayer) {
        // Filter by region 
        layerFilter += layerFilter == '' ? `region='${this.selectedExtent}' ` : ` AND region='${this.selectedExtent}' `
      }

      if (layerFilter == '') {
        url += `ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${layerType}&bbox=${extent},EPSG:3857&outputFormat=${outputFormat}&authkey=${sessionStorage.token}`
      } else {
        if (this.customerProjectId == 3) {
          layerFilter += `AND bbox(geometry, ${filterExtent})`
        } else {
          layerFilter += `AND bbox(geom, ${filterExtent})`
        }
        layerFilter += (this.selsetCQL != null && currentLayer == 'ANSI') ? ` AND ${this.selsetCQL}` : ''

        url += `ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${layerType}&outputFormat=${outputFormat}&CQL_FILTER=${layerFilter}&authkey=${sessionStorage.token}`

        url = encodeURI(url)
       
      }

      if (this.isErrorsLayer && outputFormat=='CSV') {
        // Filter by region 
        url += '&propertyName=id,lat_lon,created_user,created_date,updated_date,updated_user,project_id,layer_name,status,status_id,severity,severity_id,comments,notes,archived_date,archived,dataset,region_id,region,province,country,issue_id,layer_attribute,error_type'
      }
   
      window.open(url)
    },
    generateCQL(filterLayer = null, enableZoom = false) {
      // addRule function pushes the input values to the filter.
      this.addRule()

      let layerName = (filterLayer ? filterLayer : this.filter_layer)
        .split('_')
        .map(word => word[0].toUpperCase() + word.substring(1))
        .join(' ')
      if (
        this.mmp_layersObj[layerName] ||
        this.mmp_layersObj[this.filter_layer]
      ) {
        if (this.mmp_layersObj[layerName]) {
          filterLayer = layerName
        } else {
          filterLayer = this.filter_layer
        }

        let layerSource = this.mmp_layersObj[filterLayer].layer.getSource()
        if (this.rules === undefined || this.rules.length == 0) {
          layerSource.updateParams({
            CQL_FILTER: filterLayer == 'ANSI' ? this.selsetCQL : null
          })
          return
        }
        let layerFilter = ''
        let currentLayer
        this.rules
          .filter(r => r.layer == filterLayer || r.layer == this.filter_layer)
          .forEach(i => {
            currentLayer = ''

            // TODO: Fix the commented code below
            // if (i.property == 'created_date' || i.property == 'updated_date') {
            //   // If property is a date field
            //   currentLayer += this.getCQLDateFilterString(
            //     i.property,
            //     i.operator,
            //     i.value,
            //     i.value2
            //   )
            // } else {
            //   // Else property isn't a date field
            //   // Assumes i.operator is BETWEEN and that i.value < i.value2
            //   currentLayer = !i.value2
            //     ? `${i.property} ${i.operator} '${i.value}'`
            //     : `${i.property} >= '${i.value}' AND ${i.property} <= '${i.value2}'`
            // }
            // Assumes i.operator is BETWEEN and that i.value < i.value2


            /** 06/05/2024 WEBGIS-2806 fix left_neighbors/right_neighbors - for operator '=' it should do a relative search using operator 'like' & '%'  */
            if (i.layer == "delivery_coverage" && (i.property == 'left_neighbors' || i.property == 'right_neighbors')) {
              if (i.value.indexOf(',') > -1) {
                let values = i.value.split(',')
                i.value = values.join('%')
              }

              currentLayer = (i.operator = '=')
                ? `${i.property} like '%${i.value}%'`
                : !i.value2
                  ? `${i.property} ${i.operator} '${i.value}'`
                  : `${i.property} >= '${i.value}' AND ${i.property} <= '${i.value2}'`
            } else {
              currentLayer = !i.value2
                ? `${i.property} ${i.operator} '${i.value}'`
                : `${i.property} >= '${i.value}' AND ${i.property} <= '${i.value2}'`
            }

            layerFilter += (layerFilter == '' ? '' : ' AND ') + currentLayer
            layerFilter +=
              this.selsetCQL != null && filterLayer == 'ANSI'
                ? ` AND ${this.selsetCQL}`
                : ''
            layerSource.updateParams({
              CQL_FILTER: layerFilter
            })
          })

        if (enableZoom) {
          let index = this.maintainViewArr.findIndex(
            obj => obj.layer == filterLayer
          )

          if (index == -1) {
            // Filtered layer not found in maintainViewArr, add to array
            this.maintainViewArr.push({
              layer: filterLayer,
              viewMaintained: this.maintainView
            })
          } else {
            // Filtered layer found in maintainViewArr
            let obj = this.maintainViewArr[index]
            obj.viewMaintained = this.maintainView
          }

          /* If Maintain View checkbox isn't checked and APPLY button
          in filter modal is clicked, zoom to bound all features */
          this.zoomToGeoServerLayer(
            layerName,
            filterLayer,
            layerFilter,
            !this.maintainView && enableZoom
          )
        }

        this.$emit('rulesChanged', this.rules)

        this.$bvModal.hide('filterModal')
      }
    },
    removeFilter() {
      this.rules = this.rules.filter(r => r.layer != this.filter_layer)
      this.mmp_layersObj[this.filter_layer].layer.getSource().updateParams({
        CQL_FILTER: this.filter_layer == 'ANSI' ? this.selsetCQL : null
      })
      this.$emit('rulesChanged', this.rules)
    },
    zoomToGeoServerLayer(layerName, origLayerName, filter, enableZoom) {
      let layerSource = this.mmp_layersObj[origLayerName].layer.getSource()
      let layerType = layerSource.getParams().LAYERS
      let validFilter = ![null, undefined, ''].includes(filter)
      let url =
        layerSource.getUrls()[0] +
        'service=WFS&version=1.0.0&request=GetFeature&typeName=' +
        layerType +
        '&outputFormat=application/json&format_options=callback:getJsonFeatureExtent&authkey=' + sessionStorage.token + '&CQL_FILTER=' +
        encodeURI(filter)

      if (sessionStorage.token && typeof sessionStorage.token == "string" && validFilter) {
        axios
          .get(url, {})
          .then(response => {
            let features = new GeoJSON().readFeatures(response.data)
            if (features.length != 0) {
              if (enableZoom) {
                if (!layerType.includes('_hd')) {
                  let minZoom = this.mmp_layersObj[this.filter_layer].minZoom
                  let zoomControl = this.mmp_layersObj[this.filter_layer].zoomControl
                  let zoomValue = features.length == 1 ? this.zoomLevelCap : !zoomControl ? 3 : minZoom ? minZoom : this.zoomLevelCap
                  this.map
                    .getView()
                    .fit(
                      new transformExtent(
                        new VectorSource({features: features.slice(0, 1)}).getExtent(),
                        new projGet('EPSG:4326'),
                        new projGet('EPSG:3857')
                      ),
                      {
                        maxZoom: zoomValue
                      }
                    )
                }
              }
            } else {
              this.$bvToast.toast(
                `No features found with these filters on ${layerName} layer.`,
                {title: 'No Features Found'}
              )
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      }
    }
  },
  watch: {
    generateCQLObj: function (newValue, oldValue) {
      this.generateCQL(newValue.layer, newValue.enableZoom)
    },
    rules: function (newValue) {
      let otonomoRules = newValue.filter(rule => rule.layer == 'Otonomo')
      this.$emit('saveOtonomoRules', otonomoRules)
    }
  }
}
</script>
